// FTEDN — Firestore realtime service (onSnapshot wiring)
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  type Unsubscribe,
  type DocumentData,
} from 'firebase/firestore';
import { COLLECTIONS } from '@ftedn/shared';
import { connectFirestoreEmulatorIfNeeded, firestoreDb } from './firebase';

export const REALTIME_PATHS = {
  notifications: COLLECTIONS.NOTIFICATIONS,
  chatChannels: COLLECTIONS.CHAT_CHANNELS,
  productAssignments: COLLECTIONS.BETA_APPLICATIONS,
} as const;

export const REALTIME_FIELDS = {
  notificationOwner: 'recipientId',
  productOwner: 'userId',
  productAssignedAt: 'appliedAt',
} as const;

const activeListeners = new Map<string, Unsubscribe>();

/** Subscribe to a user's notifications in real time */
export function subscribeNotifications(
  userId: string,
  onData: (docs: DocumentData[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  unsubscribe('notifications');
  connectFirestoreEmulatorIfNeeded();

  const q = query(
    collection(firestoreDb, REALTIME_PATHS.notifications),
    where(REALTIME_FIELDS.notificationOwner, '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50),
  );

  const unsub = onSnapshot(q, (snap) => {
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onData(docs);
  }, onError);

  activeListeners.set('notifications', unsub);
  return unsub;
}

/** Subscribe to messages in a chat channel */
export function subscribeMessages(
  channelId: string,
  onData: (docs: DocumentData[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const key = `messages:${channelId}`;
  unsubscribe(key);
  connectFirestoreEmulatorIfNeeded();

  const q = query(
    collection(firestoreDb, REALTIME_PATHS.chatChannels, channelId, 'messages'),
    orderBy('createdAt', 'asc'),
    limit(100),
  );

  const unsub = onSnapshot(q, (snap) => {
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onData(docs);
  }, onError);

  activeListeners.set(key, unsub);
  return unsub;
}

/** Subscribe to a user's product assignments */
export function subscribeProducts(
  userId: string,
  onData: (docs: DocumentData[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  unsubscribe('products');
  connectFirestoreEmulatorIfNeeded();

  const q = query(
    collection(firestoreDb, REALTIME_PATHS.productAssignments),
    where(REALTIME_FIELDS.productOwner, '==', userId),
    orderBy(REALTIME_FIELDS.productAssignedAt, 'desc'),
  );

  const unsub = onSnapshot(q, (snap) => {
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onData(docs);
  }, onError);

  activeListeners.set('products', unsub);
  return unsub;
}

/** Unsubscribe a specific listener */
export function unsubscribe(key: string): void {
  const unsub = activeListeners.get(key);
  if (unsub) {
    unsub();
    activeListeners.delete(key);
  }
}

/** Unsubscribe all active listeners (call on logout) */
export function unsubscribeAll(): void {
  activeListeners.forEach(unsub => unsub());
  activeListeners.clear();
}

/** Get count of active listeners (for debugging) */
export function getActiveListenerCount(): number {
  return activeListeners.size;
}
